export const MIN_YEAR = 1900;
export const MAX_DAY = 31;
export const MONTHS_IN_YEAR = 12;

export const getMonthNames = (locale?: string): readonly string[] => {
  const formatter = new Intl.DateTimeFormat(locale, { month: 'long' });
  return Array.from({ length: MONTHS_IN_YEAR }, (_, i) =>
    formatter.format(new Date(2000, i, 1)),
  );
};

export const getDaysInMonth = (month: number, year: number): number => {
  return new Date(year, month, 0).getDate();
};

export interface DateParts {
  day: string;
  month: string;
  year: string;
}

export const parseDateString = (
  value?: string | number | readonly string[],
): DateParts => {
  if (!value || typeof value !== 'string') {
    return { day: '', month: '', year: '' };
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return { day: '', month: '', year: '' };
  }

  // Date-only strings parse as UTC midnight, so UTC getters must be used to avoid a local-timezone day shift
  return {
    day: date.getUTCDate().toString().padStart(2, '0'),
    month: (date.getUTCMonth() + 1).toString().padStart(2, '0'),
    year: date.getUTCFullYear().toString(),
  };
};

export const formatDateString = (
  day: string,
  month: string,
  year: string,
): string => {
  if (day && month && year) {
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return '';
};

export const formatLocaleStringDate = (date: Date, locale: string) =>
  date.toLocaleString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

export const padNumber = (value: number): string =>
  value.toString().padStart(2, '0');

export const isValidDay = (day: number, maxDays: number): boolean =>
  !isNaN(day) && day >= 1 && day <= maxDays;

export const isValidYear = (
  year: number,
  minYear: number,
  maxYear: number,
): boolean => !isNaN(year) && year >= minYear && year <= maxYear;

export const isValidMonth = (month: number): boolean =>
  !isNaN(month) && month >= 1 && month <= MONTHS_IN_YEAR;

export const calculateTimeRemaining = (
  expiresAt?: string,
): { days: number; hours: number; minutes: number } | null => {
  if (!expiresAt) return null;

  const now = Date.now();
  const expiry = new Date(expiresAt).getTime();
  const diff = expiry - now;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes };
};
